import React, { useState, useEffect } from 'react';
import { apiFetch } from '../App';

const fmt = n => (typeof n === 'number' ? `${n.toFixed(2)} €` : '—');
const round2 = n => Math.round(n * 100) / 100;

function getMonths() {
  const now = new Date();
  const make = (offset) => {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  return { prev: make(-1), current: make(0), next: make(1) };
}

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  return new Date(y, m - 1, 1).toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
}

function calcKpi(reservations, payments) {
  const active = reservations.filter(r => !r.cancelled && !r.warning);
  const paid   = active.filter(r => payments[r.id]);
  const unpaid = active.filter(r => !payments[r.id]);
  const sum = (arr, field) => round2(arr.reduce((s, r) => s + (r[field] ?? 0), 0));

  return {
    nb:           active.length,
    nb_cancelled: reservations.filter(r => r.cancelled).length,
    nb_paid:      paid.length,
    nb_unpaid:    unpaid.length,
    // 3 métriques principales
    encaisse:   sum(paid,   'recu_airbnb'),
    a_recevoir: sum(unpaid, 'recu_airbnb'),
    total:      sum(active, 'mon_total'),
    // Détail par catégorie
    commission: sum(active, 'ma_commission'),
    menage:     sum(active, 'cleaning_fee'),
    upsells:    sum(active, 'a_facturer'),
  };
}

function KpiCard({ label, month, kpi, loading, isCurrent }) {
  if (loading) return (
    <div className={`kpi-card${isCurrent ? ' kpi-card--current' : ''}`}>
      <div className="kpi-card__header">
        <span className="kpi-label">{label}</span>
        <span className="kpi-month">{monthLabel(month)}</span>
      </div>
      <div className="kpi-loading">Chargement...</div>
    </div>
  );
  if (!kpi) return null;

  const numSize = isCurrent ? 20 : 16;

  return (
    <div className={`kpi-card${isCurrent ? ' kpi-card--current' : ''}`}>
      <div className="kpi-card__header">
        <span className="kpi-label">{label}</span>
        <span className="kpi-month">{monthLabel(month)}</span>
      </div>

      <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
        {kpi.nb} réservation{kpi.nb !== 1 ? 's' : ''}
        {kpi.nb_cancelled > 0 && <span style={{ color:'#e53e3e' }}> · {kpi.nb_cancelled} annulée{kpi.nb_cancelled > 1 ? 's' : ''}</span>}
      </div>

      {/* 3 métriques */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:16 }}>
        <div style={{ textAlign:'center', padding:'8px 4px', background:'#f0fff4', borderRadius:8 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#276749', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:4 }}>Encaissé ✅</div>
          <div style={{ fontSize:numSize, fontWeight:800, color:'#276749' }}>{fmt(kpi.encaisse)}</div>
          {kpi.nb_paid > 0 && <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{kpi.nb_paid} résa</div>}
        </div>
        <div style={{ textAlign:'center', padding:'8px 4px', background:'#fffaf0', borderRadius:8 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#c05621', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:4 }}>À recevoir ⏳</div>
          <div style={{ fontSize:numSize, fontWeight:800, color:'#c05621' }}>{fmt(kpi.a_recevoir)}</div>
          {kpi.nb_unpaid > 0 && <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{kpi.nb_unpaid} résa</div>}
        </div>
        <div style={{ textAlign:'center', padding:'8px 4px', background:'#eef4ff', borderRadius:8 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#4F6EE8', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:4 }}>Total</div>
          <div style={{ fontSize: isCurrent ? 22 : 17, fontWeight:800, color:'#1a1a2e' }}>{fmt(kpi.total)}</div>
          <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>mensuel</div>
        </div>
      </div>

      {/* Détail par catégorie */}
      <div style={{ borderTop:'1px solid #eee', paddingTop:12, display:'flex', flexDirection:'column', gap:6 }}>
        {[
          { label:'Commission 20%',    value: kpi.commission, color:'#4F6EE8' },
          { label:'Frais de ménage',   value: kpi.menage,     color:'#38a169' },
          { label:'Upsells (à fact.)', value: kpi.upsells,    color:'#b7791f' },
          { label:'Linge',             value: 0,              color:'#888' },
          { label:'Onboarding',        value: 0,              color:'#888' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
            <span style={{ color:'#555' }}>{label}</span>
            <span style={{ fontWeight:600, color: value > 0 ? color : '#ccc' }}>
              {value > 0 ? fmt(value) : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivitySection({ title, icon, color, months, password }) {
  const [data, setData]       = useState({ prev: null, current: null, next: null });
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState({ prev: true, current: true, next: true });

  useEffect(() => {
    async function fetchAll() {
      const payRes  = await apiFetch('/payments', password);
      const payData = await payRes.json();
      setPayments(payData);

      await Promise.all(
        Object.entries(months).map(async ([key, month]) => {
          try {
            const res = await apiFetch(`/hostex/reservations?month=${month}`, password);
            const d   = await res.json();
            setData(prev => ({ ...prev, [key]: calcKpi(d.reservations ?? [], payData) }));
          } catch {
            setData(prev => ({ ...prev, [key]: { nb:0, nb_cancelled:0, nb_paid:0, nb_unpaid:0, encaisse:0, a_recevoir:0, total:0, commission:0, menage:0, upsells:0 } }));
          } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
          }
        })
      );
    }
    fetchAll();
  }, [months, password]);

  return (
    <div className="activity-section">
      <div className="activity-header" style={{ borderLeftColor: color }}>
        <span className="activity-icon">{icon}</span>
        <span className="activity-title">{title}</span>
        {data.current && (
          <span className="activity-total" style={{ color }}>
            {fmt(data.current.total)} ce mois
          </span>
        )}
      </div>
      <div className="kpi-grid">
        <KpiCard label="Mois précédent" month={months.prev}    kpi={data.prev}    loading={loading.prev}    isCurrent={false} />
        <KpiCard label="Mois en cours"  month={months.current} kpi={data.current} loading={loading.current} isCurrent={true}  />
        <KpiCard label="Mois prochain"  month={months.next}    kpi={data.next}    loading={loading.next}    isCurrent={false} />
      </div>
    </div>
  );
}

function Dashboard({ password }) {
  const months = getMonths();

  return (
    <div className="section">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize:20, fontWeight:800, color:'#1a1a2e', marginBottom:4 }}>Vue d'ensemble</h2>
        <p style={{ color:'#888', fontSize:14 }}>Suivi par activité — mois précédent, en cours, à venir</p>
      </div>

      <ActivitySection title="Conciergerie" icon="🏠" color="#4F6EE8" months={months} password={password} />

      <div className="activity-placeholder">
        <span>+ Formation L'Œil du Client</span>
        <span className="placeholder-badge">Bientôt</span>
      </div>
      <div className="activity-placeholder">
        <span>+ LCD Business Partners</span>
        <span className="placeholder-badge">Bientôt</span>
      </div>
    </div>
  );
}

export default Dashboard;
